// api-client.ts (フロントエンド用)
interface AuthConfig {
  token: string;
  baseUrl: string;
}

/**
 * 画像生成オプションのインターフェース
 */
export interface GenerateImageOptions {
  prompt: string;
  size?: string;
  style?: string;
  quality?: string;
}

/**
 * 画像生成レスポンスのインターフェース
 */
export interface GenerateImageResponse {
  success: boolean;
  images: string[];
  created: number;
  error?: string;
}

/**
 * 音声ナレーションオプションのインターフェース
 */
export interface GenerateVoiceOptions {
  text: string;
  voice_id: string;
  stability?: number;
  similarity_boost?: number;
  style?: number;
}

/**
 * 音楽生成オプションのインターフェース
 */
export interface GenerateMusicOptions {
  prompt: string;
  duration?: number;
  temperature?: number;
  top_k?: number;
  top_p?: number;
}

/**
 * 音楽生成レスポンスのインターフェース
 */
export interface GenerateMusicResponse {
  music_url: string;
  request_id: string;
}

/**
 * 動画生成オプションのインターフェース
 */
export interface GenerateVideoOptions {
  prompt: string;
  negative_prompt?: string;
  num_frames?: number;
  fps?: number;
  width?: number;
  height?: number;
  guidance_scale?: number;
  seed?: number | null;
}

/**
 * 動画生成リクエスト開始レスポンスのインターフェース
 */
export interface VideoGenerationStartResponse {
  success: boolean;
  prediction_id: string;
  error?: string;
}

/**
 * 動画生成状態チェックレスポンスのインターフェース
 */
export interface VideoGenerationStatusResponse {
  status: "processing" | "succeeded" | "failed";
  output?: string | string[];
  error?: string;
}

/**
 * 単一のAPI Workerと通信するAPIクライアント
 */
export class ApiClient {
  private readonly token: string;
  private readonly baseUrl: string;
  private readonly userId: string;

  constructor(auth: AuthConfig, userId: string) {
    this.token = auth.token;
    this.baseUrl = auth.baseUrl;
    this.userId = userId;
  }

  private async makeRequest<T>(
    endpoint: string,
    method: string = "GET",
    data?: any
  ): Promise<T> {
    // リクエストオプションを設定
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      },
    };

    // リクエストデータにユーザーIDを追加
    const requestData = {
      ...data,
      userId: this.userId,
    };

    // POSTリクエストの場合はボディを追加
    if (method === "POST" && data) {
      options.body = JSON.stringify(requestData);
    }

    // リクエストを送信
    const response = await fetch(`${this.baseUrl}/${endpoint}`, options);

    // ステータスが200以外の場合はエラー
    if (!response.ok) {
      // JSONレスポンスの場合はエラー詳細を取得
      if (response.headers.get("content-type")?.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          `API request failed: ${response.status} ${
            response.statusText
          } - ${JSON.stringify(errorData)}`
        );
      }
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`
      );
    }

    // コンテントタイプに基づいてレスポンスを処理
    const contentType = response.headers.get("Content-Type") || "";

    if (contentType.includes("application/json")) {
      return response.json();
    } else if (
      contentType.includes("audio") ||
      contentType.includes("video") ||
      contentType.includes("octet-stream")
    ) {
      return response.blob() as unknown as T;
    } else {
      return response.text() as unknown as T;
    }
  }

  /**
   * API使用状況を取得
   */
  async getApiUsage(): Promise<any> {
    const action: string = "usage";
    return this.makeRequest<any>(`usage/${action}`, "GET");
  }

  /**
   * 画像生成APIを呼び出し
   */
  async generateImage(
    options: GenerateImageOptions
  ): Promise<GenerateImageResponse> {
    const action: string = "generate";
    return this.makeRequest<GenerateImageResponse>(
      `image/${action}`,
      "POST",
      options
    );
  }

  /**
   * 音声ナレーション生成APIを呼び出し
   */
  async generateVoice(options: GenerateVoiceOptions): Promise<Blob> {
    const action: string = "generate";
    return this.makeRequest<Blob>(`voice/${action}`, "POST", options);
  }

  /**
   * 音楽生成APIを呼び出し
   */
  async generateMusic(
    options: GenerateMusicOptions
  ): Promise<GenerateMusicResponse> {
    const action: string = "generate";
    return this.makeRequest<GenerateMusicResponse>(
      `music/${action}`,
      "POST",
      options
    );
  }

  /**
   * 動画生成リクエストを開始
   */
  async startVideoGeneration(
    options: GenerateVideoOptions
  ): Promise<VideoGenerationStartResponse> {
    const action: string = "generate";
    return this.makeRequest<VideoGenerationStartResponse>(
      `video/${action}`,
      "POST",
      options
    );
  }

  /**
   * 動画生成の状態を確認
   */
  async checkVideoGenerationStatus(
    predictionId: string
  ): Promise<VideoGenerationStatusResponse> {
    const action: string = `$check?id=${predictionId}`;
    return this.makeRequest<VideoGenerationStatusResponse>(
      `video/${action}`,
      "GET"
    );
  }
}
